import { useState } from "react";
import { useNavigate, useSearchParams, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, CheckCircle2, FileText } from "lucide-react";
import { Logo, LogoMark } from "@/components/Logo";
import { SoundWaves } from "@/components/SoundWaves";
import { useAuth } from "@/context/AuthContext";
import { COUNTRIES } from "@/lib/countries";
import { createAccessRequest, appleSessionLogin, AFM_ORIGIN, deviceInfo } from "@/lib/afmApi";
import { ArtistPicker } from "@/components/ArtistPicker";
import { PasswordResetFlow } from "@/components/PasswordResetFlow";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const CONTRACT = [
  ["1. Reparto 70/30", "El artista/catálogo percibe el 70% de las regalías netas generadas. Always Fresh Music retiene el 30% por servicios de distribución, tecnología e infraestructura."],
  ["2. Sin costos ocultos", "No cobramos cuotas anuales, mantenimientos ni cargos por lanzamiento para tu perfil de artista."],
  ["3. Split Sheets", "El reparto entre colaboradores se define mediante Split Sheets; cada colaborador cobra de forma autónoma el porcentaje asignado y aceptado."],
  ["4. Propiedad de los másters", "El modelo 70/30 aplica solo sobre regalías netas; la titularidad de los másters permanece 100% en manos del artista."],
  ["5. Declaración de derechos", "Al aceptar declaras poseer los derechos de autor de los fonogramas que distribuyes y que la información entregada es veraz."],
];

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
const AppleIcon = () => (
  <svg viewBox="0 0 384 512" className="h-4 w-4" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

export default function Login() {
  const { user, signIn, signInWith } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [artistName, setArtistName] = useState("");
  const [artistPick, setArtistPick] = useState([]);
  const [country, setCountry] = useState("");
  const [dialCode, setDialCode] = useState("+");
  const [phone, setPhone] = useState("");
  const [social, setSocial] = useState("");
  const [accept, setAccept] = useState(false);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleGoogle = () => {
    // Full-page navigation to SONA's server-side Google OAuth (the backend, outside Venezuela,
    // talks to Google). It redirects back to {origin}/auth/callback#token=... or #error=...
    const dev = deviceInfo().device_id || "";
    const url = `${AFM_ORIGIN}/api/auth/google?platform=afm&origin=${encodeURIComponent(window.location.origin)}&device_id=${encodeURIComponent(dev)}`;
    window.location.assign(url);
  };
  const APPLE_SERVICE_ID = process.env.REACT_APP_APPLE_SERVICE_ID;
  const handleApple = async () => {
    if (!APPLE_SERVICE_ID) {
      toast.message("Inicio con Apple", { description: "Pendiente: falta el Apple Service ID de SONA y registrar el dominio de AFM en Apple." });
      return;
    }
    try {
      if (!window.AppleID) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
          s.onload = res; s.onerror = rej; document.body.appendChild(s);
        });
      }
      window.AppleID.auth.init({ clientId: APPLE_SERVICE_ID, scope: "email name", redirectURI: window.location.origin + "/auth/callback", usePopup: true });
      const resp = await window.AppleID.auth.signIn();
      const idToken = resp?.authorization?.id_token;
      if (!idToken) throw new Error("sin token");
      const au = resp?.user || {};
      const appleEmail = au.email;
      const appleName = au.name ? `${au.name.firstName || ""} ${au.name.lastName || ""}`.trim() : undefined;
      const u = await appleSessionLogin(idToken, appleName, appleEmail);
      signInWith(u);
      toast.success(`Bienvenido, ${u.name || u.email}`);
      navigate("/dashboard");
    } catch (e) {
      if (e?.error === "popup_closed_by_user") return;
      toast.error(e?.response?.data?.detail || "No se pudo iniciar sesión con Apple");
    }
  };

  if (user) return <Navigate to="/dashboard" replace />;
  const isRegister = mode === "register";

  if (submitted) return (
    <div className="flex min-h-screen items-center justify-center bg-white px-6" data-testid="access-request-confirmation">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-7 w-7" /></div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">Always Fre$h Music</div>
        <h1 className="font-serif text-3xl font-bold tracking-tight text-black">¡Solicitud enviada con éxito!</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">Tu perfil está en proceso de revisión por el equipo de Always Fresh Music. Te notificaremos por correo electrónico una vez que tu acceso sea aprobado.</p>
        <button onClick={() => { setSubmitted(false); setMode("login"); }} data-testid="back-to-login" className="mt-8 rounded-lg bg-black px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800">Volver al inicio de sesión</button>
      </div>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        if (!name || !email || !password) { toast.error("Completa nombre, email y contraseña"); setLoading(false); return; }
        if (!country) { toast.error("Selecciona tu país de residencia"); setLoading(false); return; }
        if (!accept) { toast.error("Debes aceptar el Contrato de Distribución para continuar"); setLoading(false); return; }
        const phoneFull = phone.trim() ? `${dialCode} ${phone.trim()}`.trim() : "";
        await createAccessRequest({
          name: name.trim(), email: email.trim(), password,
          artist_name: artistName.trim(), country, phone: phoneFull,
          social_link: social.trim(), accepted_terms: true,
          spotify_id: artistPick[0]?.spotify_id, spotify_url: artistPick[0]?.spotify_url,
          apple_id: artistPick[0]?.apple_id, apple_url: artistPick[0]?.apple_url, artist_image: artistPick[0]?.image,
        });
        setSubmitted(true);
        setLoading(false);
        return;
      } else {
        if (!email || !password) { setLoading(false); return; }
        const res = await signIn(email.trim(), password);
        if (res?.mfa) { navigate("/verificar"); return; }
        toast.success("Bienvenido de vuelta");
      }
      navigate("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      let msg = err?.response?.data?.detail;
      if (!msg) msg = status === 403 ? "Esta cuenta no tiene acceso a Always Fresh Music" : "Credenciales inválidas";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-transparent bg-[#F4F4F5] px-4 py-3 text-sm text-black outline-none shadow-[inset_3px_3px_7px_rgba(0,0,0,0.05),inset_-3px_-3px_7px_rgba(255,255,255,0.9)] transition-all placeholder:text-slate-400 focus:border-[#9E1B22]/40 focus:bg-white";

  return (
    <div className="flex min-h-screen bg-white">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-black p-12 text-white lg:flex">
        <SoundWaves className="absolute inset-0 h-full w-full opacity-80" />
        <Link to="/" className="relative z-10 inline-flex"><LogoMark className="h-14 w-auto text-white" /></Link>
        <div className="relative z-10">
          <h2 className="font-serif text-5xl font-extrabold leading-[1.05] tracking-tight">
            La mesa de trabajo <span className="italic text-[#E8A0A4]">pesada</span> para tu música.
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-300">
            Analíticas profundas, libro contable, exportaciones Excel/CSV, catálogo
            y split sheets. Todo tu negocio musical en un solo lugar.
          </p>
        </div>
        <div className="relative z-10 text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Tecnología por SONA</div>
        {/* Ambient diffuse glows only — clean focus on horizontal waves + typography */}
        <div className="pointer-events-none absolute -right-28 top-12 h-96 w-96 rounded-full blur-3xl" style={{ backgroundColor: "rgba(158,27,34,0.35)" }} />
        <div className="pointer-events-none absolute -bottom-20 left-10 h-72 w-72 rounded-full blur-3xl" style={{ backgroundColor: "rgba(158,27,34,0.18)" }} />
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 lg:hidden"><Link to="/"><Logo imgClassName="h-12 w-auto object-contain" /></Link></div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">Portal de Distribución</div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-black">
            {isRegister ? "Solicitar acceso" : "Iniciar sesión"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {isRegister ? "Solicita acceso a Always Fresh Music. Revisaremos tu perfil y te avisaremos por correo cuando sea aprobado." : "Accede con tu cuenta unificada de SONA."}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            {isRegister && (
              <>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre completo</label>
                  <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre artístico <span className="text-slate-400">(opcional)</span></label>
                  <ArtistPicker single value={artistPick} testId="register-artist" ph="Busca tu perfil en Spotify / Apple Music…"
                    onChange={(arr) => { setArtistPick(arr); setArtistName(arr[0]?.name || ""); }} />
                  <p className="mt-1 text-[11px] text-slate-500">Vincula tu perfil de Spotify para que tus lanzamientos se asocien al artista correcto.</p>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">País de residencia</label>
                  <select
                    data-testid="register-country-select"
                    value={country}
                    onChange={(e) => {
                      const c = e.target.value;
                      setCountry(c);
                      const found = COUNTRIES.find((x) => x.name === c);
                      if (found && found.dial !== "+") setDialCode(found.dial);
                    }}
                    className={inputCls}
                  >
                    <option value="">Selecciona tu país…</option>
                    {COUNTRIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Teléfono celular <span className="text-slate-400">(opcional)</span></label>
                  <div className="flex w-full items-center rounded-lg border border-slate-300 bg-white transition-colors focus-within:border-black">
                    <select data-testid="register-dial-select" value={dialCode} onChange={(e) => setDialCode(e.target.value)} className="shrink-0 cursor-pointer rounded-l-lg border-0 bg-transparent py-3 pl-4 pr-2 text-sm outline-none">
                      {[...new Set(COUNTRIES.map((c) => c.dial).filter((d) => d !== "+"))].sort().map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <span className="h-5 w-px shrink-0 bg-slate-200" />
                    <input data-testid="register-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d\s-]/g, ""))} placeholder="414 123 4567" className="min-w-0 flex-1 border-0 bg-transparent py-3 pl-3 pr-4 text-sm outline-none" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Red social / Spotify / Apple Music <span className="text-slate-400">(opcional)</span></label>
                  <input data-testid="register-social-input" value={social} onChange={(e) => setSocial(e.target.value)} placeholder="https://open.spotify.com/artist/…" className={inputCls} />
                </div>
              </>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" className={inputCls} autoComplete="email" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Contraseña</label>
              <div className="relative">
                <input data-testid="login-password-input" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={`${inputCls} pr-11`} autoComplete={isRegister ? "new-password" : "current-password"} />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black" data-testid="toggle-password-visibility">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {!isRegister && (
              <div className="text-right">
                <button type="button" data-testid="forgot-password-link" onClick={() => setForgotOpen(true)} className="text-xs font-semibold text-[#9E1B22] transition-opacity hover:opacity-70">¿Olvidaste tu contraseña?</button>
              </div>
            )}

            {isRegister && (
              <label data-testid="register-terms-label" className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
                <input data-testid="register-terms-checkbox" type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-black" />
                <span>Acepto el{" "}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setContractOpen(true); }}
                    data-testid="register-contract-link"
                    className="cursor-pointer font-semibold text-[#9E1B22] underline underline-offset-2 transition-opacity hover:opacity-70"
                  >
                    Contrato de Distribución de Always Fresh Music
                  </button>{" "}
                  y declaro poseer los derechos de autor de mis fonogramas.</span>
              </label>
            )}

            <Dialog open={contractOpen} onOpenChange={setContractOpen}>
              <DialogContent className="max-w-lg" data-testid="contract-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 font-serif text-xl">
                    <FileText className="h-5 w-5 text-[#9E1B22]" /> Contrato de Distribución 70/30
                  </DialogTitle>
                  <DialogDescription>Lee los términos antes de aceptar. Conservas el 100% de tus másters.</DialogDescription>
                </DialogHeader>
                <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1" data-testid="contract-modal-body">
                  {CONTRACT.map(([h, b]) => (
                    <div key={h}>
                      <h4 className="text-sm font-semibold text-black">{h}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{b}</p>
                    </div>
                  ))}
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <button
                    type="button"
                    onClick={() => setContractOpen(false)}
                    data-testid="contract-modal-close"
                    className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-black"
                  >
                    Cerrar
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAccept(true); setContractOpen(false); }}
                    data-testid="contract-modal-accept"
                    className="rounded-lg bg-[#9E1B22] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    He leído y acepto
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
              <DialogContent className="max-w-md" data-testid="forgot-password-modal">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">Recuperar contraseña</DialogTitle>
                  <DialogDescription>Te enviaremos un código a tu correo para crear una nueva contraseña.</DialogDescription>
                </DialogHeader>
                <PasswordResetFlow defaultEmail={email} onDone={() => setForgotOpen(false)} />
              </DialogContent>
            </Dialog>

            <button type="submit" data-testid="login-submit-button" disabled={loading} className="neu-btn group flex w-full items-center justify-center gap-2 rounded-xl bg-black px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60">
              {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : (<>{isRegister ? "Enviar solicitud" : "Entrar"}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>)}
            </button>
          </form>

          {!isRegister && (
            <div className="mt-5" data-testid="social-login">
              <div className="relative my-4 flex items-center">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">o continúa con</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <button type="button" data-testid="google-login-btn" onClick={handleGoogle} className="flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-black transition-colors hover:border-black">
                  <GoogleIcon /> Google
                </button>
              </div>
              <p className="mt-2 text-center text-[11px] text-slate-400">Solo para cuentas ya registradas y aprobadas.</p>
            </div>
          )}

          <button
            data-testid="toggle-auth-mode"
            onClick={() => setMode(isRegister ? "login" : "register")}
            className="mt-5 text-sm text-slate-500 hover:text-black"
          >
            {isRegister ? "¿Ya tienes cuenta? Inicia sesión" : "¿No tienes cuenta? Solicita distribución"}
          </button>
        </div>
      </div>
    </div>
  );
}
